/**
 * App Version Create Dialog Component
 *
 * Dialog for creating a new app version with comprehensive form validation.
 * Includes all version configuration fields with proper validation.
 */

import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateAppVersion } from '@/hooks/use-app-versions';
import { useToast } from '@/hooks/use-toast';
import type { Platform, Priority } from '@/types/app-version';

interface AppVersionCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/**
 * Form Schema
 * Validation schema for create form with platform-specific store URL requirements
 */
const createFormSchema = z
  .object({
    appBundleId: z.string().min(1, 'App Bundle ID is required'),
    version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Must be valid semver (e.g., 1.2.3)'),
    platform: z.enum(['ios', 'android', 'both']),
    isMandatory: z.boolean().default(false),
    priority: z.enum(['low', 'medium', 'high', 'critical']).default('medium'),
    minSupportedVersion: z.string().optional(),
    storeUrlIos: z.string().url().optional().or(z.literal('')),
    storeUrlAndroid: z.string().url().optional().or(z.literal('')),
    messageTitle: z.string().optional(),
    messageDescription: z.string().optional(),
    actionButtonText: z.string().default('Update Now'),
    dismissButtonText: z.string().optional().or(z.literal('')),
    releaseNotes: z.string().optional(),
    isActive: z.boolean().default(true),
    isBeta: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // Platform-specific store URL validation
    if (data.platform === 'ios' || data.platform === 'both') {
      if (!data.storeUrlIos) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'iOS Store URL is required for iOS and Both platforms',
          path: ['storeUrlIos'],
        });
      }
    }
    if (data.platform === 'android' || data.platform === 'both') {
      if (!data.storeUrlAndroid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Android Store URL is required for Android and Both platforms',
          path: ['storeUrlAndroid'],
        });
      }
    }
    // Dismiss button validation
    if (data.isMandatory && data.dismissButtonText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Dismiss button not allowed for mandatory updates',
        path: ['dismissButtonText'],
      });
    }
  });

type CreateFormValues = z.infer<typeof createFormSchema>;

/**
 * App Version Create Dialog
 * Renders comprehensive form for creating new app version
 */
export function AppVersionCreateDialog({
  open,
  onOpenChange,
  onSuccess,
}: AppVersionCreateDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createAppVersionMutation = useCreateAppVersion();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      platform: 'both',
      priority: 'medium',
      isMandatory: false,
      isActive: true,
      isBeta: false,
      actionButtonText: 'Update Now',
    },
  });

  const watchedPlatform = watch('platform');
  const watchedIsMandatory = watch('isMandatory');

  const onSubmit = async (data: CreateFormValues) => {
    setIsSubmitting(true);
    try {
      await createAppVersionMutation.mutateAsync({
        appBundleId: data.appBundleId,
        version: data.version,
        platform: data.platform,
        isMandatory: data.isMandatory,
        priority: data.priority,
        minSupportedVersion: data.minSupportedVersion || undefined,
        storeUrlIos: data.storeUrlIos || undefined,
        storeUrlAndroid: data.storeUrlAndroid || undefined,
        messageTitle: data.messageTitle || undefined,
        messageDescription: data.messageDescription || undefined,
        actionButtonText: data.actionButtonText || 'Update Now',
        dismissButtonText: data.dismissButtonText || undefined,
        releaseNotes: data.releaseNotes || undefined,
        isActive: data.isActive,
        isBeta: data.isBeta,
      });

      toast({
        title: 'Version Created',
        description: `App version ${data.version} has been created successfully`,
      });

      reset();
      onSuccess();
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: error instanceof Error ? error.message : 'Failed to create version',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={open => {
        if (!isSubmitting) {
          onOpenChange(open);
          if (!open) reset();
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create App Version
          </DialogTitle>
          <DialogDescription>
            Define a new app version with update requirements and messaging
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="appBundleId">App Bundle ID *</Label>
                <Input
                  id="appBundleId"
                  {...register('appBundleId')}
                  placeholder="com.example.app"
                  disabled={isSubmitting}
                />
                {errors.appBundleId && (
                  <p className="text-xs text-destructive">{errors.appBundleId.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  {...register('version')}
                  placeholder="1.0.0"
                  disabled={isSubmitting}
                />
                {errors.version && (
                  <p className="text-xs text-destructive">{errors.version.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="platform">Platform *</Label>
                <Select
                  value={watchedPlatform}
                  onValueChange={value => setValue('platform', value as Platform)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="platform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ios">iOS</SelectItem>
                    <SelectItem value="android">Android</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={watch('priority')}
                  onValueChange={value => setValue('priority', value as Priority)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="minSupportedVersion">Minimum Supported Version</Label>
              <Input
                id="minSupportedVersion"
                {...register('minSupportedVersion')}
                placeholder="1.0.0"
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Versions below this will be forced to update
              </p>
            </div>
          </div>

          {/* Store URLs Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Store URLs</h3>

            {(watchedPlatform === 'ios' || watchedPlatform === 'both') && (
              <div className="space-y-2">
                <Label htmlFor="storeUrlIos">iOS App Store URL *</Label>
                <Input
                  id="storeUrlIos"
                  {...register('storeUrlIos')}
                  placeholder="https://apps.apple.com/..."
                  disabled={isSubmitting}
                />
                {errors.storeUrlIos && (
                  <p className="text-xs text-destructive">{errors.storeUrlIos.message}</p>
                )}
              </div>
            )}

            {(watchedPlatform === 'android' || watchedPlatform === 'both') && (
              <div className="space-y-2">
                <Label htmlFor="storeUrlAndroid">Google Play Store URL *</Label>
                <Input
                  id="storeUrlAndroid"
                  {...register('storeUrlAndroid')}
                  placeholder="https://play.google.com/..."
                  disabled={isSubmitting}
                />
                {errors.storeUrlAndroid && (
                  <p className="text-xs text-destructive">{errors.storeUrlAndroid.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Messaging Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Update Messaging</h3>

            <div className="space-y-2">
              <Label htmlFor="messageTitle">Message Title</Label>
              <Input
                id="messageTitle"
                {...register('messageTitle')}
                placeholder="Update Available"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="messageDescription">Message Description</Label>
              <Textarea
                id="messageDescription"
                {...register('messageDescription')}
                placeholder="A new version is available with bug fixes and improvements"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="actionButtonText">Action Button Text</Label>
                <Input
                  id="actionButtonText"
                  {...register('actionButtonText')}
                  placeholder="Update Now"
                  disabled={isSubmitting}
                />
              </div>

              {!watchedIsMandatory && (
                <div className="space-y-2">
                  <Label htmlFor="dismissButtonText">Dismiss Button Text</Label>
                  <Input
                    id="dismissButtonText"
                    {...register('dismissButtonText')}
                    placeholder="Later"
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="releaseNotes">Release Notes</Label>
              <Textarea
                id="releaseNotes"
                {...register('releaseNotes')}
                placeholder="What's new in this version..."
                rows={4}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Status Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Status</h3>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isMandatory"
                  checked={watchedIsMandatory}
                  onCheckedChange={checked => setValue('isMandatory', checked as boolean)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="isMandatory" className="text-sm font-normal cursor-pointer">
                  Mandatory update (users cannot dismiss)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={watch('isActive')}
                  onCheckedChange={checked => setValue('isActive', checked as boolean)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="isActive" className="text-sm font-normal cursor-pointer">
                  Active (enabled for version checks)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isBeta"
                  checked={watch('isBeta')}
                  onCheckedChange={checked => setValue('isBeta', checked as boolean)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="isBeta" className="text-sm font-normal cursor-pointer">
                  Beta version
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Version
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
